import type { CanonicalEntity } from '@/shared/api/types';
import * as THREE from 'three';
import type { UniversalBundle } from '@/shared/api/types';
import { FULL_FRAME_BAND, type VisibleBand } from './viewportInsets';

/**
 * ЧЕМ ЗАДАН ФРОНТ. Стены как выбираемого объекта развёртки в продукте нет: направление
 * «спереди» вьюер считает по БОЛЬШИНСТВУ поворотов модулей. Пока это число оставалось внутри
 * `bundleFrontCameraZ`, развёртка выглядела привязанной к стене, которую никто не выбирал.
 * Здесь оно вынесено наружу, чтобы экран мог назвать эту опору словами.
 *
 * `aligned` — сколько модулей смотрят в ТУ ЖЕ сторону, что выбранный фронт; `total` — сколько
 * модулей вообще объявили поворот.
 */
export function bundleFrontCameraBasis(
  entities: readonly CanonicalEntity[] | null | undefined,
): { frontZ: 1 | -1; aligned: number; total: number } {
  const rotations = (entities ?? [])
    .filter((entity) => entity.kind === 'module')
    .map((entity) => Number((entity.domain as Record<string, unknown> | undefined)?.rotationYDeg))
    .filter(Number.isFinite);
  if (!rotations.length) return { frontZ: 1, aligned: 0, total: 0 };
  const rotated = rotations.filter((degrees) => {
    const normalized = ((degrees % 360) + 360) % 360;
    return normalized > 90 && normalized < 270;
  }).length;
  const frontZ: 1 | -1 = rotated > rotations.length / 2 ? -1 : 1;
  return {
    frontZ,
    aligned: frontZ === -1 ? rotated : rotations.length - rotated,
    total: rotations.length,
  };
}

export function bundleFrontCameraZ(bundle: UniversalBundle | null | undefined): 1 | -1 {
  return bundleFrontCameraBasis(bundle?.canonicalScene?.entities).frontZ;
}

export function entityBox(entity: CanonicalEntity): THREE.Box3 | null {
  const a = entity.render?.aabb;
  if (a?.min && a?.max) {
    const box = new THREE.Box3(
      new THREE.Vector3(a.min[0], a.min[1], a.min[2]),
      new THREE.Vector3(a.max[0], a.max[1], a.max[2]),
    );
    // Box3.isEmpty() does NOT catch NaN; a single non-finite aabb from the
    // backend would poison the camera-framing union → NaN camera → black
    // screen. Reject boxes with any non-finite component instead.
    return isFiniteBox(box) ? box : null;
  }
  const s = entity.render?.size;
  const c = entity.render?.center;
  if (s && c) {
    const box = new THREE.Box3(
      new THREE.Vector3(c[0] - s[0] / 2, c[1] - s[1] / 2, c[2] - s[2] / 2),
      new THREE.Vector3(c[0] + s[0] / 2, c[1] + s[1] / 2, c[2] + s[2] / 2),
    );
    return isFiniteBox(box) ? box : null;
  }
  return null;
}

function isFiniteBox(box: THREE.Box3): boolean {
  return (
    Number.isFinite(box.min.x) &&
    Number.isFinite(box.min.y) &&
    Number.isFinite(box.min.z) &&
    Number.isFinite(box.max.x) &&
    Number.isFinite(box.max.y) &&
    Number.isFinite(box.max.z)
  );
}

export function sceneBounds(scene: THREE.Object3D): { box: THREE.Box3; center: THREE.Vector3; size: THREE.Vector3 } {
  const box = new THREE.Box3();
  box.setFromObject(scene);
  if (!isFinite(box.min.x) || box.isEmpty()) {
    box.set(new THREE.Vector3(-1000, 0, -1000), new THREE.Vector3(1000, 2000, 1000));
  }
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  return { box, center, size };
}

/** Context-padding multiplier for camera framing, scaled by scene size.
 * Single cabinets want breathing room (2.05); large multi-module compositions
 * (kitchen runs / wardrobe walls ≳ 3 m) framed at 2.05 look tiny — tighten as
 * the scene grows.  Single source of truth shared by the canonical-fit path
 * (useGltfLoader) and applyProjection (useThreeScene) so they can't drift. */
export function cameraFramePadding(maxDimMm: number, showRoomShell = false): number {
  if (showRoomShell) return maxDimMm > 5200 ? 1.18 : 1.34;
  if (maxDimMm > 4000) return 1.18;
  if (maxDimMm > 2400) return 1.5;
  return 2.05;
}

/** Margin left around an exact fit, as a multiplier on the fitted distance.
 *
 * Distinct from `cameraFramePadding`, which pads a `maxDim`-based distance in
 * the projection presets.  Here the distance is already the exact fit, so the
 * number means what it says: 1.06 ⇒ 6 % of breathing room. */
export function cameraFitMargin(showRoomShell = false): number {
  // The interactive viewport wants a little orbit room; the room shell needs
  // more so its walls are not clipped by the frame edge.
  return showRoomShell ? 1.22 : 1.12;
}

/**
 * Вписать коробку в кадр перспективной камеры.
 *
 * @param band ВИДИМАЯ ПОЛОСА кадра — та его часть, которую не закрывает хром
 *             оболочки (лента этапов снизу, остров навигации сверху, орбиты по
 *             бокам). По умолчанию — весь кадр, и тогда считается ровно то же,
 *             что считалось до появления полосы. Замер полосы — дело
 *             `viewportInsets.measureVisibleBand`; здесь только математика.
 */
export function fitCameraToBox(
  camera: THREE.PerspectiveCamera,
  controls: { target: THREE.Vector3; update(): void },
  box: THREE.Box3,
  padding = 1.06,
  direction?: THREE.Vector3,
  band: VisibleBand = FULL_FRAME_BAND,
) {
  const center = box.getCenter(new THREE.Vector3());
  // 29.09.2026 — премиум-изометрия прототипа: выше и под углом ~35°
  const dir = direction?.clone().normalize() ?? new THREE.Vector3(1, 1.15, 0.85).normalize();

  // Frame the box by what the camera ACTUALLY sees, not by its diagonal.
  //
  // The previous math used the bounding sphere (`size.length() / 2`), which is
  // the worst possible estimate for furniture: a kitchen with two parallel runs
  // is very deep in Z but short in Y, so the diagonal ran far ahead of the
  // silhouette and the model landed at roughly a third of the viewport height.
  // (3000 × 2900 × 3615 mm ⇒ diagonal 5521 mm against a visible height of 2900.)
  // Projecting the eight corners onto the camera's own right/up axes costs a few
  // dot products and frames every shape — deep, wide or tall — the same way.
  const forward = dir.clone().negate();
  const worldUp = Math.abs(forward.y) > 0.999 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(forward, worldUp).normalize();
  const up = new THREE.Vector3().crossVectors(right, forward).normalize();

  const fov = (camera.fov * Math.PI) / 180;
  const tanHalfFov = Math.tan(fov / 2);
  const aspect = camera.aspect > 0 ? camera.aspect : 1;

  // Solve the smallest distance that keeps every corner inside the VISIBLE BAND.
  //
  // Раньше здесь решалась задача «уместить восемь углов во фрустум», то есть во
  // ВЕСЬ кадр. Кадр у Luminous закрыт сверху островом и снизу лентой этапов, и
  // модель честно центрировалась в холсте, из которого четверти не видно.
  // Полоса `band` описывает видимую часть кадра в NDC: `halfX`/`halfY` — во
  // сколько раз она уже кадра, `centerX`/`centerY` — куда она смещена.
  //
  // Вывод. Камера стоит в `T + dir·d`, где цель `T = center − up·a − right·b`
  // уведена так, чтобы ЦЕНТР коробки спроецировался в центр полосы:
  //   a = centerY · tan(fov/2) · d,   b = centerX · tan(fov/2) · aspect · d.
  // Тогда угол `p` (от центра коробки) сидит на глубине `d + p·forward`, а его
  // отклонение от центра полосы по вертикали равно
  //   (p·up − centerY·tan(fov/2)·p·forward) / (tan(fov/2)·(d + p·forward)),
  // и оно обязано уложиться в `halfY`. Множители `a` и `b` при этом
  // сокращаются — остаётся честное неравенство на одно неизвестное `d`:
  //   d ≥ |p·up − centerY·tan·dep| / (halfY·tan) − dep,
  //   d ≥ |p·right − centerX·tan·aspect·dep| / (halfX·tan·aspect) − dep.
  // При открытом холсте (halfX = halfY = 1, centerX = centerY = 0) обе строки
  // вырождаются в прежнюю формулу до последнего знака — поэтому пины на
  // вписывание и на ортографию остаются зелёными, а не «перекалиброванными».
  //
  // Solving per corner (rather than padding by half the depth) matters for
  // deep shapes: a far corner's own depth already buys it room, so the run
  // does not get pushed away for clearance it never needed.
  const halfY = band.halfY > 0 ? band.halfY : 1;
  const halfX = band.halfX > 0 ? band.halfX : 1;
  const tanUp = tanHalfFov * halfY;
  const tanRight = tanHalfFov * aspect * halfX;
  let dist = 1;
  for (let corner = 0; corner < 8; corner += 1) {
    const point = new THREE.Vector3(
      corner & 1 ? box.max.x : box.min.x,
      corner & 2 ? box.max.y : box.min.y,
      corner & 4 ? box.max.z : box.min.z,
    ).sub(center);
    const depthOffset = point.dot(forward);
    dist = Math.max(
      dist,
      Math.abs(point.dot(up) - band.centerY * tanHalfFov * depthOffset) / tanUp - depthOffset,
      Math.abs(point.dot(right) - band.centerX * tanHalfFov * aspect * depthOffset) / tanRight - depthOffset,
    );
  }
  dist = Math.max(1, dist * padding);

  // СМЕЩЕНИЕ ЦЕЛИ — ЧАСТЬ РЕШЕНИЯ, А НЕ УКРАШЕНИЕ.
  //
  // Одним отдалением не отделаться: закрытый снизу кадр не просто меньше — его
  // видимая часть ещё и лежит выше середины. `OrbitControls` на каждом кадре
  // разворачивает камеру на `target`, поэтому единственный способ поднять
  // модель над лентой — увести ЦЕЛЬ вниз ровно на столько, на сколько полоса
  // поднята вверх. При открытом холсте оба смещения — ноль, и цель остаётся в
  // центре коробки, как и была.
  const target = center.clone()
    .addScaledVector(up, -(band.centerY * tanHalfFov * dist))
    .addScaledVector(right, -(band.centerX * tanHalfFov * aspect * dist));

  camera.position.copy(target.clone().add(dir.multiplyScalar(dist)));
  camera.near = Math.max(1, dist / 200);
  camera.far = dist * 8;
  camera.updateProjectionMatrix();
  controls.target.copy(target);
  controls.update();
}
